import {
  FSAdapterInterface,
  FSDownloadOptions,
  FSStatResult,
} from "@citychallenge/core";
import axios from "axios";

import { getStorage } from "./get-storage";

/**
 * Special class that implements an object cache around session storage.
 *
 * This is because in incognito more or special browser privacy modes sessionStorage
 * may not always work as expected, and a fallback into an object cache can be
 * quite handly.
 *
 * See Sentry issue:
 * https://sentry.io/share/issue/75a141fe40a64b7a9a540e5583842ba4/
 */
class CachedSessionStorage {
  private _cache: { [key: string]: string } = {};

  public getItem(key: string) {
    return this._cache[key] || getStorage("sessionStorage").getItem(key);
  }

  public setItem(key: string, value: string) {
    this._cache[key] = value;
    getStorage("sessionStorage").setItem(key, value);
  }
}

export class FSAdapter implements FSAdapterInterface {
  public CachesDirectoryPath = "";

  private cachedStorage = new CachedSessionStorage();

  public async exists(filepath: string) {
    // TODO: Implement

    if (filepath.endsWith(".json")) {
      return !!this.cachedStorage.getItem(filepath);
    }

    // Just return true for now, because we have no caching implemented yet
    return true;
  }

  public async stat(filepath: string): Promise<FSStatResult> {
    // TODO: Implement
    return {
      path: filepath,
      size: "100000", // 100kb placeholder
    };
  }

  public async mkdir(_filepath: string) {
    // This is a noop in the Cache system
    return;
  }

  public downloadFile(options: FSDownloadOptions) {
    // TODO: Implement
    const cachedStorage = this.cachedStorage;
    if (options.toFile.endsWith(".json")) {
      const jobId = Math.floor(Math.random() * 9999999999);

      async function downloadJson() {
        const { data, status, headers } = await axios.get(options.fromUrl);

        const jsonFile = JSON.stringify(data);

        cachedStorage.setItem(options.toFile, jsonFile);

        return {
          jobId,
          statusCode: status,
          bytesWritten: +headers["content-length"],
        };
      }
      return {
        jobId,
        promise: downloadJson(),
      };
    }

    return {
      jobId: -1,
      promise: Promise.resolve({
        jobId: -1,
        statusCode: 200,
        bytesWritten: 10000,
      }),
    };
  }

  public async readFile(filepath: string, _encodingOrOptions?: any) {
    if (filepath.endsWith(".json")) {
      const item = this.cachedStorage.getItem(filepath);

      if (!item) throw new Error("File does not exist");

      return item;
    }
    throw new Error("Cannot read this file.");
  }

  public async unlink(_filepath: string) {
    // TODO: Implement
    return;
  }
}
